/**
 * ProxySelectField — reusable scaffolder field extension.
 *
 * Fetches options from a Backstage proxy endpoint and renders a select dropdown.
 * Designed to be reused across many templates — configure entirely via ui:options.
 *
 * Template usage example:
 *
 *   awsAccounts:
 *     type: string
 *     ui:field: ProxySelectField
 *     ui:options:
 *       proxy: /aws-sso-accounts-list        # proxy path key in app-config
 *       path: /dev/accounts                  # API path appended after proxy target
 *       params:                              # query params — values are other form field names
 *         accountType: accountType
 *       valueKey: id                         # key in each item to use as the form value
 *       labelKey: name                       # key in each item to use as display label
 *       arrayKey: accounts                   # if response is { accounts: [...] }, set this
 *       title: AWS Account
 *       placeholder: Choose an AWS Account
 *
 * If the response is a plain string array, omit valueKey/labelKey/arrayKey.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';

interface ProxySelectOptions {
  proxy: string;
  path?: string;
  params?: Record<string, string>; // key = query param name, value = form field name to read
  valueKey?: string;
  labelKey?: string;
  arrayKey?: string;
  title?: string;
  placeholder?: string;
}

type SelectItem = { value: string; label: string };

function resolveItems(data: unknown, opts: ProxySelectOptions): SelectItem[] {
  let arr: unknown[] = [];

  if (Array.isArray(data)) {
    arr = data;
  } else if (opts.arrayKey && typeof data === 'object' && data !== null) {
    const nested = (data as Record<string, unknown>)[opts.arrayKey];
    if (Array.isArray(nested)) arr = nested;
  }

  return arr.map(item => {
    if (typeof item === 'string') return { value: item, label: item };
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;
      const value = String(opts.valueKey ? (obj[opts.valueKey] ?? '') : (obj.id ?? obj.value ?? JSON.stringify(obj)));
      const label = String(opts.labelKey ? (obj[opts.labelKey] ?? value) : value);
      return { value, label };
    }
    return { value: String(item), label: String(item) };
  });
}

export const ProxySelectField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');
  const opts = useMemo(
    () => (uiSchema?.['ui:options'] ?? {}) as unknown as ProxySelectOptions,
    [uiSchema],
  );

  const [items, setItems] = useState<SelectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // Build query string, resolving param values from other form fields.
  const buildUrl = useCallback(() => {
    if (!opts.proxy) return null;
    const base = `${backendBaseUrl}/api/proxy${opts.proxy}${opts.path ?? ''}`;
    if (!opts.params || Object.keys(opts.params).length === 0) return base;

    const formValues = formContext?.formData ?? {};
    const qs = new URLSearchParams();
    for (const [queryParam, fieldName] of Object.entries(opts.params)) {
      const val = formValues[fieldName];
      if (val !== undefined && val !== null && val !== '') {
        qs.set(queryParam, String(val));
      } else {
        return null; // dependency not yet selected — skip fetch
      }
    }
    const qsStr = qs.toString();
    return qsStr ? `${base}?${qsStr}` : base;
  }, [opts, formContext?.formData, backendBaseUrl]);

  // Re-fetch whenever the dependent form field values change.
  const depValues = opts.params
    ? Object.values(opts.params)
        .map(f => formContext?.formData?.[f])
        .join('|')
    : '';

  useEffect(() => {
    let cancelled = false;

    const url = buildUrl();
    if (!url) {
      // Dependency not yet selected or no longer valid — reset this field.
      setItems([]);
      setError(undefined);
      if (formData) onChange(undefined);
      return () => { cancelled = true; };
    }

    setLoading(true);
    setError(undefined);

    fetchApi
      .fetch(url)
      .then(async res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!cancelled) {
          setItems(resolveItems(data, opts));
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(String(err.message ?? err));
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depValues, opts.proxy, opts.path]);

  const hasError = Boolean(rawErrors?.length) || Boolean(error);
  const title = opts.title ?? 'Select';
  const placeholder = opts.placeholder ?? `Choose ${title}`;

  return (
    <FormControl fullWidth error={hasError} required={required} variant="outlined">
      <InputLabel shrink>{title}</InputLabel>
      <Select
        value={formData ?? ''}
        label={title}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
        displayEmpty
      >
        {loading && (
          <MenuItem disabled value="">
            <CircularProgress size={16} style={{ marginRight: 8 }} /> Loading…
          </MenuItem>
        )}
        {!loading && items.length === 0 && (
          <MenuItem disabled value="">
            {error ? `Error: ${error}` : placeholder}
          </MenuItem>
        )}
        {items.map(item => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      {(rawErrors ?? []).map((e, i) => (
        <FormHelperText key={i}>{e}</FormHelperText>
      ))}
    </FormControl>
  );
};
